// Form field: label + control + hint + server error, wired with ids so the
// error is announced (aria-describedby / aria-invalid). Server `fields[]`
// problems are mapped onto fields by path (see api/errors.ts).
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cn } from "./cn";

interface FieldProps {
  readonly label: string;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  readonly className?: string;
  readonly children: ReactElement<{ id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean }>;
}

export function Field({ label, hint, error, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        ...(describedBy ? { "aria-describedby": describedBy } : {}),
        ...(error ? { "aria-invalid": true } : {}),
      })
    : children;
  return (
    <div className={cn("grid content-start gap-1", className)}>
      <label htmlFor={id} className="text-xs font-medium text-fg">
        {label}
      </label>
      {control}
      {hint ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
